import { useCallback, useEffect, useRef, useState } from 'react';
import { format } from 'date-fns';
import { revogarUrl, urlDaMidia } from '@/services/midiaService';
import { registrarErro, tratarErro } from '@/lib/erros';

/**
 * ★★★ WX — OS DOIS PRAZOS DO DOWNLOAD, e por que NÃO é um só.
 *
 * ⚠⚠ Medido no bucket em 08/out (`docs/medicao-wx-midia.md` §5): **4.825 arquivos**, mediana
 * **0,06 MB**, p90 **0,40 MB**, p99 **4,88 MB** — ★★★ e o **maior tem 41,42 MB**.
 *
 * ⚠⚠⚠ Um prazo curto único **estaria errado aqui**: 41 MB numa conexão de clínica a 250 KB/s
 * leva **~165 s** de download **legítimo**. ★ O prazo de 10 s da WV transformaria esse sucesso
 * em "falhou" — e a tela **não sabe o tamanho**: `midia.tamanho` é **nulo para mídia recebida**
 * (medido em 06/out, 1.951 de 1.951).
 *
 * ★★ Daí dois estágios, e **só o segundo é desistência**:
 *
 * | | |
 * |---|---|
 * | **AVISAR (8 s)** | o texto muda e **aparece o botão** — ⚠⚠ o download **CONTINUA**. ★ Quem baixa 41 MB abre normalmente; quem está no limbo ganha ação na mão |
 * | **DESISTIR (90 s)** | aí sim é falha, com botão e registro na Saúde |
 *
 * ★ 8 s cobre o p99 (4,88 MB) com folga em conexão comum. ★ 90 s ainda cabe um arquivo de
 * 10 MB a 250 KB/s (40 s) — ⚠ 41 MB **não** cabe, e é exatamente por isso que o estágio de 8 s
 * **não cancela nada**: o caso raro segue por baixo e abre.
 */
export const AVISAR_MS = 8_000;
export const DESISTIR_MS = 90_000;

/**
 * ★★ WX — o prazo do "o servidor ainda está preparando".
 *
 * ⚠ `pronta: false` não tinha prazo nenhum: *"Recebendo mídia…"* era o MESMO limbo com outro
 * texto. ★ 60 s porque o gatilho normal leva segundos; ⚠ e medido em produção, **as 5
 * mensagens** presas há mais de 10 min **todas têm `midia.erro`** — o servidor, quando desiste,
 * marca. ★ Então este prazo é para o caso em que ele **não** marcou.
 */
export const PREPARANDO_MS = 60_000;

/** ★ Os textos. Exportados porque os testes leem daqui, não de uma cópia digitada. */
export const TEXTO_BAIXANDO = 'Abrindo mídia…';
export const TEXTO_DEMOROU = 'Ainda baixando… arquivos grandes podem demorar';
export const TEXTO_FALHOU = 'Não conseguimos carregar este arquivo';
export const TEXTO_FALHOU_DESISTIU = 'Não conseguimos carregar este arquivo — avise o suporte.';
export const TEXTO_PREPARANDO = 'O servidor ainda está preparando este arquivo';

/** Extensão tirada do caminho do Storage — o nome do arquivo costuma faltar. */
const extensaoDe = (caminho) => {
  const ponto = caminho ? caminho.lastIndexOf('.') : -1;
  return ponto > 0 ? caminho.slice(ponto) : '';
};

const ROTULO_TIPO = {
  imagem: 'imagem',
  audio: 'audio',
  video: 'video',
  documento: 'arquivo',
};

const carimboDe = (valor) => {
  const data = typeof valor?.toDate === 'function' ? valor.toDate() : null;
  return data ? format(data, 'yyyy-MM-dd_HH-mm') : 'sem-data';
};

/**
 * Nome com que o arquivo chega ao computador do atendente.
 *
 * A Evolution só manda `fileName` em documento; áudio e imagem chegam sem nome
 * nenhum. Sem isto o download sairia "null" ou sem extensão — e uma pasta cheia
 * de arquivos indistinguíveis é o mesmo que arquivo perdido. Daí o carimbo de
 * data e hora da mensagem: "imagem-2026-08-18_18-17.jpeg".
 */
function nomeParaBaixar(mensagem) {
  const { midia, tipo } = mensagem;
  if (midia.nomeArquivo) return midia.nomeArquivo;
  const base = ROTULO_TIPO[tipo] ?? 'arquivo';
  return `${base}-${carimboDe(mensagem.enviadaEm)}${extensaoDe(midia.caminho)}`;
}

/**
 * Botão de baixar. `stopPropagation` porque na miniatura ele fica sobre a
 * imagem, cujo clique abre o lightbox: sem isso um clique dispararia os dois.
 */
function BotaoBaixar({ url, nome, className, children }) {
  return (
    <a
      className={className}
      href={url}
      download={nome}
      title={`Baixar ${nome}`}
      onClick={(e) => e.stopPropagation()}
    >
      {children}
    </a>
  );
}

function Aviso({ texto, motivo, carregando }) {
  return (
    <span className={carregando ? 'msg-midia carregando' : 'msg-midia indisponivel'} title={motivo}>
      {texto}
    </span>
  );
}

/**
 * ★★ WX — espera (ou falha) COM AÇÃO: um componente só para os quatro estados que oferecem
 * "Tentar de novo".
 *
 * ⚠ Antes o botão existia solto dentro de UM ramo, e foi por isso que ele nunca apareceu nos
 * estados de pendência. ★ Com um componente, acrescentar um estado novo **não** é acrescentar
 * um botão novo.
 */
function EsperaComAcao({ texto, aoTentar, motivo, carregando = false }) {
  return (
    <span className={carregando ? 'msg-midia carregando' : 'msg-midia indisponivel'} title={motivo}>
      {texto}
      <button type="button" className="msg-midia-tentar" onClick={aoTentar}>
        Tentar de novo
      </button>
    </span>
  );
}

function Imagem({ url, legenda, nome }) {
  const dialogo = useRef(null);
  // Neutro: o mesmo componente desenha a imagem que chegou e a que a clínica
  // enviou. "Imagem recebida" mentiria para o leitor de tela na metade dos
  // casos, agora que a saída também tem mídia.
  const descricao = legenda || 'Imagem da conversa';

  // Clique no backdrop: o alvo do evento é o próprio <dialog>, não o conteúdo.
  const talvezFechar = (e) => {
    if (e.target === dialogo.current) dialogo.current.close();
  };

  return (
    <>
      <span className="msg-imagem-caixa">
        <img
          className="msg-imagem"
          src={url}
          alt={descricao}
          onClick={() => dialogo.current?.showModal()}
        />
        {/* Mesma URL do blob que a miniatura já usa: baixar não busca de novo. */}
        <BotaoBaixar url={url} nome={nome} className="msg-baixar">
          ⬇
        </BotaoBaixar>
      </span>

      <dialog className="lightbox" ref={dialogo} onClick={talvezFechar}>
        <button className="lightbox-fechar" onClick={() => dialogo.current?.close()}>
          ✕
        </button>
        <BotaoBaixar url={url} nome={nome} className="lightbox-baixar">
          ⬇ Baixar
        </BotaoBaixar>
        <img src={url} alt={descricao} />
      </dialog>
    </>
  );
}

/**
 * Render da mídia de uma mensagem.
 *
 * São quatro estados, não dois — a distinção entre "ainda baixando" e "nunca vai
 * baixar" é o que evita spinner eterno:
 *
 * - `pronta: true`  → arquivo no Storage, buscamos a URL e mostramos.
 * - `pronta: false` → o gatilho está baixando (segundos). O listener do
 *   Firestore é realtime: quando virar `true`, isto re-renderiza sozinho.
 * - `midia.erro`    → o gatilho desistiu (expirado, muito grande, sem número…).
 * - `pronta` ausente → mensagem anterior à Etapa 1, sem `jidRemoto`. Nunca foi
 *   processada e nunca será (decisão consciente de não retrocarregar).
 */
export default function MidiaMensagem({ mensagem }) {
  const { midia, tipo, texto } = mensagem;
  /**
   * ★★★ WX (ajuste de 09/out) — A IDENTIDADE DA MENSAGEM, e ela é o par id+caminho.
   *
   * ⚠ Só o `caminho` não bastaria: duas mensagens podem, em teoria, apontar para o mesmo
   * arquivo (encaminhamento). ⚠⚠ Só o `id` também não: ele é o mesmo documento se a mídia for
   * reprocessada e o caminho mudar. ★ O par é o que identifica **o que esta bolha está
   * mostrando**.
   */
  const chaveAlvo = `${mensagem?.id ?? ''}|${midia?.caminho ?? ''}`;
  const pronta = midia?.pronta;
  const caminho = midia?.caminho;

  const [url, setUrl] = useState(null);
  const [falhouDownload, setFalhouDownload] = useState(false);
  /**
   * ★★★ WU/U4 — A TRAVA DE MÃO ÚNICA FOI DESFEITA.
   *
   * ⚠⚠ Antes: `falhouDownload` virava `true` e **nunca voltava**. As dependências do efeito eram
   * `[caminho, pronta]` — nenhuma muda numa nova tentativa —, então **a única saída era remontar
   * o componente**. ★★ `tentativa` entra nas dependências: incrementá-la **é** a nova tentativa.
   * ⚠ `falhas` conta as seguidas, para avisar o suporte depois de 3.
   */
  const [tentativa, setTentativa] = useState(0);
  const [falhas, setFalhas] = useState(0);
  /**
   * ★★★ WX — A FASE DA ESPERA: `'baixando'` → `'demorou'` (8 s) → `'desistiu'` (90 s).
   *
   * ★ Um estado só, com três valores, em vez de dois booleanos: ⚠ dois booleanos admitem o
   * estado impossível "demorou e desistiu ao mesmo tempo", e a tela teria de escolher.
   */
  const [fase, setFase] = useState('baixando');
  /** ★★ WX — o servidor passou de `PREPARANDO_MS` com `pronta: false` e sem `erro`. */
  const [preparandoDemais, setPreparandoDemais] = useState(false);

  /**
   * ★★★ WX — A GUARDA DE GERAÇÃO. **É o conserto que não aparece na tela.**
   *
   * ⚠⚠ `getBlob` **não tem cancelamento** — nem `abort`, nem sinal. Então, depois de um
   * "Tentar de novo", a resposta da tentativa **antiga** continua viva e vai chegar.
   *
   * ⚠⚠⚠ Sem guarda, ela faria duas coisas erradas:
   * 1. **sobrescrever** o resultado da tentativa nova (`setUrl` da antiga chegando por último);
   * 2. ★★★ pior — se a pessoa trocou de mensagem e o componente foi **reaproveitado** pelo
   *    React, a resposta antiga abriria **a mídia de outra mensagem**. ⚠ Um áudio de paciente
   *    na bolha de outro paciente é vazamento de dado, não defeito de tela.
   *
   * ## ★★★ DUAS GUARDAS, porque sucesso e falha têm regras DIFERENTES (ajuste de 09/out)
   *
   * ★★ **Sucesso — por IDENTIDADE.** A resposta atrasada **pode** abrir a mídia, desde que a
   * bolha ainda esteja mostrando **a mesma mensagem** (`chaveAlvo` = id + caminho).
   * ⚠⚠ Era isto que faltava: antes, aos 90 s eu **abandonava** a tentativa, e como o `getBlob`
   * **não retoma de onde parou**, cada "Tentar de novo" recomeçava do zero — ★★★ um arquivo de
   * 41 MB num link lento **nunca** conseguia abrir. ★ Agora ele abre quando chegar.
   *
   * ★★ **Primeira que chega, ganha.** Se houve "Tentar de novo" na mesma mensagem, as duas
   * tentativas são válidas pela identidade — ⚠ então `refAberto` registra qual chave **já
   * abriu**, e a segunda resposta é descartada (com o blob revogado).
   *
   * ★★★ **Falha — por GERAÇÃO.** ⚠ Uma falha ANTIGA chegando enquanto uma tentativa nova está
   * em voo não pode pintar a tela de "não conseguimos": ela é notícia velha. ★ Para o `.catch`,
   * vale a geração corrente, como antes.
   *
   * ⚠ Os dois em `useRef` e não `useState`: mudá-los **não pode** re-renderizar, senão
   * realimentam o efeito.
   */
  const refGeracao = useRef(0);
  /** ★ A identidade que a bolha está mostrando AGORA. `null` = desmontada. */
  const refAlvo = useRef(null);
  /** ★ A identidade que JÁ abriu — a segunda resposta da mesma mensagem é descartada. */
  const refAberto = useRef(null);

  useEffect(() => {
    if (pronta !== true || !caminho) return undefined;

    const geracao = (refGeracao.current += 1);
    /** ★ Só a geração CORRENTE pode anunciar FALHA. */
    const minhaVez = () => refGeracao.current === geracao;
    /**
     * ★★★ A TRAVA OBRIGATÓRIA: a resposta só escreve se a bolha ainda mostra ESTA mensagem.
     * ⚠⚠ Sem ela, uma resposta atrasada abriria **a mídia de outro paciente** na bolha nova —
     * é o que a mutação de "tirar a checagem de mesma mensagem" faz, e o teste da troca de
     * conversa a mata.
     */
    const mesmaMensagem = () => refAlvo.current === chaveAlvo;
    refAlvo.current = chaveAlvo;
    let criada = null;

    setFase('baixando');
    setUrl(null);

    // ★★ ESTÁGIO 1 — avisar, SEM cancelar. ⚠⚠ Nada de `setFalhouDownload` aqui.
    const avisar = setTimeout(() => { if (minhaVez()) setFase('demorou'); }, AVISAR_MS);

    // ★★ ESTÁGIO 2 — desistir.
    const desistir = setTimeout(() => {
      if (!minhaVez()) return;
      setFase('desistiu');
      /**
       * ★★★ REGISTRO NA 1ª VEZ, ao contrário da falha de rede.
       *
       * ⚠ A regra da U4 — só registrar na 3ª falha seguida — existe porque o Wi-Fi da clínica
       * oscila e a 1ª falha é ruído. ★★ **Noventa segundos sem resposta não é oscilação**: é um
       * download que não volta, e é exatamente o que ninguém conseguia ver.
       *
       * ⚠⚠ O `caminho` **NUNCA** entra: ele é
       * `clinicas/…/conversas/resgate__+5562…/midia/…` — **contém o telefone do paciente**.
       * ★ Vão só a FORMA e o tempo: tipo, extensão, tamanho quando conhecido.
       * ⚠ `midia.tamanho` é **nulo para mídia recebida** (medido 06/out) — daí o `?? 'desconhecido'`.
       */
      const kb = Number.isFinite(Number(midia?.tamanho))
        ? `${Math.round(Number(midia.tamanho) / 1024)}kB`
        : 'desconhecido';
      registrarErro({
        origem: 'service',
        codigo: 'abrir-midia:tempo-limite',
        mensagem: `Midia ${tipo} ${extensaoDe(caminho)} tamanho ${kb} nao respondeu em ${DESISTIR_MS / 1000}s`,
        // ★ O caminho inteiro vai aqui, e o `erros.js` manda `extra` SÓ para o console local.
        extra: { caminho },
      });
      /**
       * ★★★ E ELA **NÃO** ABANDONA A TENTATIVA — mudança aprovada pelo dono em 09/out.
       *
       * ⚠⚠ A versão anterior fazia `refGeracao.current += 1` aqui, e isso tinha um custo que o
       * ataque do bloco A mediu: ★ o `getBlob` **não retoma**, então cada "Tentar de novo"
       * recomeça do zero, e o arquivo de **41,42 MB** num link abaixo de ~3,8 Mbit/s
       * **nunca** conseguiria. ★★★ Agora o download segue, e se chegar **a mídia abre e o
       * aviso de falha sai da tela**.
       */
    }, DESISTIR_MS);

    urlDaMidia(caminho)
      .then((u) => {
        // ★★★ TRAVA 1 — trocou de mensagem/conversa, ou desmontou: descarta.
        // ⚠ Revoga o blob órfão: sem isto ele fica na memória da aba até o reload.
        if (!mesmaMensagem()) {
          revogarUrl(u);
          return;
        }
        // ★★ TRAVA 2 — outra tentativa desta MESMA mensagem já abriu: a primeira ganha.
        if (refAberto.current === chaveAlvo) {
          revogarUrl(u);
          return;
        }
        refAberto.current = chaveAlvo;
        criada = u;
        setUrl(u);
        /**
         * ★★★ Chegou DEPOIS dos 90 s? Então o aviso de falha tem de SAIR da tela.
         * ⚠ Sem estas duas linhas o ramo de falha continuaria ganhando do `url` no render, e a
         * pessoa veria "não conseguimos" com a mídia já na memória.
         */
        setFalhouDownload(false);
        setFase('baixando');
        // ★ Chegou: a contagem de falhas seguidas zera.
        setFalhas(0);
      })
      .catch((e) => {
        if (!minhaVez()) return;
        setFalhouDownload(true);
        setFalhas((n) => {
          const total = n + 1;
          /**
           * ★★★ Só a TERCEIRA falha seguida vai para a tela de Saúde.
           * ⚠⚠ Registrar a primeira encheria `/admin/saude` com o Wi-Fi da clínica oscilando —
           * ★ e é a lição do `erros.js`: **ruído mata o sinal**.
           * ★★ WX: o **tempo limite** não segue esta regra (ver acima) — ele é outro sinal.
           */
          if (total === 3) tratarErro('a mídia da mensagem', null, 'service')(e);
          return total;
        });
      })
      .finally(() => {
        clearTimeout(avisar);
        clearTimeout(desistir);
      });

    return () => {
      // ★★ Invalida esta geração: uma FALHA que chegue depois não pinta a tela.
      refGeracao.current += 1;
      /**
       * ★★★ E zera o alvo. ⚠⚠ É isto que faz a resposta atrasada ser **descartada** quando a
       * pessoa trocou de conversa — e não aberta na bolha errada.
       * ★ A reexecução do efeito (um "Tentar de novo") grava o alvo de novo logo em seguida,
       * então a retentativa na MESMA mensagem continua valendo.
       */
      refAlvo.current = null;
      clearTimeout(avisar);
      clearTimeout(desistir);
      revogarUrl(criada);
    };
    // ⚠ `tentativa` É a dependência que faz o botão funcionar. Tirá-la devolve a trava.
  }, [caminho, pronta, tentativa, tipo, midia?.tamanho, chaveAlvo]);

  /**
   * ★★ WX — o prazo do SERVIDOR (`pronta: false`).
   *
   * ★ Efeito próprio porque não depende do download: o que se espera aqui é o gatilho
   * `processarMidiaRecebida` virar `pronta: true`, e o listener do Firestore re-renderiza sozinho.
   * ⚠ Se ele morrer sem marcar `erro`, a tela ficava em *"Recebendo mídia…"* para sempre.
   */
  useEffect(() => {
    setPreparandoDemais(false);
    if (pronta !== false || midia?.erro) return undefined;
    const relogio = setTimeout(() => setPreparandoDemais(true), PREPARANDO_MS);
    return () => clearTimeout(relogio);
  }, [pronta, midia?.erro, tentativa]);

  /**
   * ★★ WX — "Tentar de novo", um só para todos os estados de espera.
   *
   * ★ Incrementar `tentativa` reexecuta **os dois** efeitos; a guarda de geração cuida de a
   * resposta antiga não voltar. ⚠ Zerar `fase` aqui evita um piscar de "desistiu" antes do
   * efeito reassentar.
   */
  const tentarDeNovo = useCallback(() => {
    setFalhouDownload(false);
    setPreparandoDemais(false);
    setFase('baixando');
    setTentativa((n) => n + 1);
  }, []);

  if (!midia) return null;

  if (midia.erro) {
    return <Aviso texto="Mídia indisponível" motivo={`Falha no download: ${midia.erro}`} />;
  }

  if (pronta === undefined) {
    return (
      <Aviso
        texto="Mídia não disponível"
        motivo="Mensagem anterior ao download automático de mídia; o arquivo não foi guardado."
      />
    );
  }

  if (pronta === false) {
    /**
     * ★★ WX — "Recebendo mídia…" GANHOU PRAZO E SAÍDA.
     *
     * ⚠⚠ Antes não havia nenhum: se o gatilho morresse sem marcar `erro`, esta frase ficava
     * para sempre. ★ Passados `PREPARANDO_MS`, a tela diz o que está esperando **e oferece
     * recarregar a leitura**.
     */
    if (preparandoDemais) {
      return <EsperaComAcao texto={TEXTO_PREPARANDO} aoTentar={tentarDeNovo} motivo="O servidor não terminou de preparar o arquivo." />;
    }
    return <Aviso texto="Recebendo mídia…" motivo="O arquivo está sendo baixado." carregando />;
  }

  if (falhouDownload || fase === 'desistiu') {
    /**
     * ★★★ O AVISO TEM AÇÃO — E AGORA ELA NÃO DESAPARECE.
     *
     * ⚠ Medido na WU: **não há cache de mídia a furar** e **não há URL que expira** — o que
     * faltava era tentar outra vez.
     *
     * ⚠⚠ WX mudou uma decisão da U4: antes, na **3ª** falha seguida o botão **desaparecia**
     * ("avise o suporte"). ★★★ **Tirar a ação de quem já tentou três vezes é tirá-la de quem
     * mais precisa** — e quem tentou três vezes é justamente quem está sem a mídia há mais
     * tempo. ★ Decisão do dono (09/out): **o botão fica**, e o texto é que muda.
     */
    const desistiu = falhas >= 3;
    return (
      <EsperaComAcao
        texto={desistiu ? TEXTO_FALHOU_DESISTIU : TEXTO_FALHOU}
        aoTentar={tentarDeNovo}
        motivo={`Falhas seguidas: ${falhas}`}
      />
    );
  }

  if (!url) {
    /**
     * ★★★ WX — OS DOIS ESTÁGIOS NA TELA.
     *
     * ★ Antes de `AVISAR_MS`: a mesma frase de sempre, sem botão — ⚠ oferecer ação no primeiro
     * instante convidaria a clicar antes de o download normal terminar (mediana: 0,06 MB).
     * ★★ Depois: o texto **explica a demora** e o botão aparece — ⚠⚠ **e o download continua**.
     */
    if (fase === 'demorou') {
      return <EsperaComAcao texto={TEXTO_DEMOROU} aoTentar={tentarDeNovo} motivo="O download continua em andamento." carregando />;
    }
    return <Aviso texto={TEXTO_BAIXANDO} motivo="Baixando o arquivo." carregando />;
  }

  if (tipo === 'audio') {
    return <audio className="msg-audio" controls src={url} />;
  }

  if (tipo === 'imagem') {
    return <Imagem url={url} legenda={texto} nome={nomeParaBaixar(mensagem)} />;
  }

  // Áudio e vídeo não ganham botão próprio: o menu nativo do <video>/<audio> já
  // traz "baixar".
  if (tipo === 'video') {
    return <video className="msg-video" controls src={url} />;
  }

  // Documento e qualquer tipo novo que o normalizador venha a trazer: um link
  // sempre serve, ainda que sem preview.
  return (
    <BotaoBaixar url={url} nome={nomeParaBaixar(mensagem)} className="msg-documento">
      ⬇ Baixar {nomeParaBaixar(mensagem)}
    </BotaoBaixar>
  );
}
